import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import './Add.css';
import authClient from '../utils/authClient';
import '../utils/loadingStyles.css';

// List of available emulators in the system
const AVAILABLE_EMULATORS = [
  { value: 'ejs', label: 'EmulatorJS' },
  { value: 'ruffle', label: 'Ruffle (Flash)' },
  { value: 'apple2ts', label: 'Apple II TS' },
  { value: 'html', label: 'HTML (Web Games)' },
  { value: 'jsdos', label: 'JS-DOS (DOS Games)' },
  { value: 'emularity', label: 'Emularity (MAME)' },
  { value: 'encrusted', label: 'Encrusted' },
  { value: 'freej2me', label: 'FreeJ2ME (Java ME)' }
];

// Define available style options
const AVAILABLE_STYLES = [
  { value: 'retro', label: 'Retro' },
  { value: 'shelf', label: 'Shelf' },
  { value: 'ios', label: 'iOS' },
  { value: '4399', label: '4399' },
  { value: 'modern', label: 'Modern' },
  { value: 'symbian', label: 'Symbian' }
];

const AddPlatform = () => {
  const navigate = useNavigate();
  
  // Default control JSON structure - must be declared before useState
  const defaultControlsJson = {
    controls: {
      player1: [
        { key: '↑', action: '上' },
        { key: '↓', action: '下' },
        { key: '←', action: '左' },
        { key: '→', action: '右' },
        { key: 'Space', action: '动作' }
      ],
      player2: [
        { key: 'W', action: '上' },
        { key: 'S', action: '下' },
        { key: 'A', action: '左' },
        { key: 'D', action: '右' },
        { key: 'G', action: '动作' }
      ]
    },
    notices: [
      { text: '提示：使用模拟器下方操作栏以进行全屏，设置键位，和存读档操作', type: 'hint' }
    ],
    credits: [
      {
        title: '模拟器',
        logo: '',
        links: [
          { label: '官方网站', url: '' },
          { label: 'GitHub', url: '' }
        ]
      }
    ]
  };
  
  // All useState hooks must be declared before any conditional logic
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [platform, setPlatform] = useState({
    namechn: '',
    nameeng: '',
    emulator: 'ejs',  // Default emulator
    emulatoroptions: '',
    description: '',
    img: '',
    ctrljson: '',
    style: 'retro',  // Default style
    defaultyear: '',  // Add the defaultyear field
    timeline: []  // Add timeline field
  });
  const [controlsJson, setControlsJson] = useState(defaultControlsJson);
  const [activeSection, setActiveSection] = useState('controls'); // For UI tabs
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [compressImage, setCompressImage] = useState(false);
  const [logoFiles, setLogoFiles] = useState({}); // For credit logo uploads
  const [error, setError] = useState(''); // Add state for error messages
  const [availableCredits, setAvailableCredits] = useState([]); // All credits from database
  const [selectedCreditIds, setSelectedCreditIds] = useState([]); // Selected credit IDs for this platform
  const [newCredits, setNewCredits] = useState([]); // New credits being created inline
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      if (!authClient.isAuthenticated()) {
        // Redirect to login page with current location for redirect after login
        navigate('/login', { state: { from: { pathname: '/add-platform' } } });
      } else {
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // Fetch available credits from database
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await apiClient.get('/credits');
        setAvailableCredits(res.data);
      } catch (err) {
        console.error('Error fetching credits:', err);
      }
    };
    
    fetchCredits();
  }, []);
  
  // Only render the form if user is authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-loading-container">
        <h2>正在验证身份...</h2>
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }



  // Helper to get default emulator-specific JSON
  const getDefaultJsonForEmulator = (emulatorType) => {
    let defaultJson = JSON.parse(JSON.stringify(defaultControlsJson));
    if (emulatorType === 'ruffle') {
      defaultJson.controls.player1 = [
        { key: '鼠标', action: '点击交互' }
      ];
      defaultJson.controls.player2 = [];
    } else if (emulatorType === 'apple2ts') {
      defaultJson.controls.player1 = [
        { key: '键盘', action: '输入' },
        { key: '鼠标', action: '交互' }
      ];
      defaultJson.controls.player2 = [];
    } else if (emulatorType === 'html') {
      defaultJson.controls.player1 = [
        { key: '根据游戏不同', action: '操作方式可能有所不同' }
      ];
      defaultJson.controls.player2 = [];
    } else if (emulatorType === 'jsdos') {
      defaultJson.controls.player1 = [
        { key: '方向键', action: '移动' },
        { key: 'Enter', action: '确认' },
        { key: 'Esc', action: '返回/菜单' },
        { key: 'Alt', action: '特殊功能' },
        { key: 'Ctrl', action: '动作' }
      ];
      defaultJson.controls.player2 = [];
      defaultJson.notices = [
        { text: '提示：JS-DOS 模拟器支持键盘快捷键, 点击窗口可捕获键盘输入', type: 'hint' },
        { text: '若要退出全屏或释放键盘捕获, 请按 Esc 键', type: 'hint' }
      ];
      defaultJson.credits = [
        {
          title: 'JS-DOS 模拟器',
          logo: '',
          links: [
            { label: '官方网站', url: 'https://js-dos.com' },
            { label: 'GitHub', url: 'https://github.com/js-dos/emulators' }
          ]
        }
      ];
    } else if (emulatorType === 'emularity') {
      defaultJson.controls.player1 = [
        { key: '方向键', action: '方向' },
        { key: 'Z', action: '按钮1' },
        { key: 'X', action: '按钮2' },
        { key: 'C', action: '按钮3' },
        { key: '5', action: '投币' },
        { key: '1', action: '开始' }
      ];
      defaultJson.controls.player2 = [];
      defaultJson.notices = [
        { text: '提示：使用Emularity模拟器的按键根据游戏可能有所不同', type: 'hint' },
        { text: '若要退出全屏，请按Esc键', type: 'hint' }
      ];
      defaultJson.credits = [
        {
          title: 'Emularity MAME模拟器',
          logo: '',
          links: [
            { label: '官方网站', url: 'https://github.com/db48x/emularity' },
            { label: 'GitHub', url: 'https://github.com/db48x/emularity' }
          ]
        }
      ];
    } else if (emulatorType === 'encrusted') {
      defaultJson.controls.player1 = [];
      defaultJson.controls.player2 = [];
      defaultJson.notices = [
        { text: '若要退出全屏，请按Esc键', type: 'hint' }
      ];
      defaultJson.credits = [
      ];
    } else if (emulatorType === 'freej2me') {
      defaultJson.controls.player1 = [
        { key: '方向键', action: '移动' },
        { key: 'Enter', action: '确认 (OK)' },
        { key: 'Q / F1', action: '左软键' },
        { key: 'W / F2', action: '右软键' },
        { key: '0-9', action: '数字键' },
        { key: 'E', action: '*' },
        { key: 'R', action: '#' },
        { key: 'Esc', action: '模拟器设置' }
      ];
      defaultJson.controls.player2 = [];
      defaultJson.notices = [
        { text: '提示：按Esc键可进入FreeJ2ME模拟器设置，调整屏幕大小和手机类型', type: 'hint' }
      ];
      defaultJson.credits = [
        {
          title: 'FreeJ2ME',
          logo: '',
          links: [
            { label: 'GitHub', url: 'https://github.com/zb3/freej2me-web' }
          ]
        }
      ];
    }
    return defaultJson;
  };

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'emulator') {
      // When emulator changes, update the default JSON too
      setControlsJson(getDefaultJsonForEmulator(value));
      setPlatform(prev => ({ ...prev, [name]: value }));
    } else {
      setPlatform(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setFileName(file.name);
      setFileSize(formatFileSize(file.size));
      // Create a temporary URL for preview
      const url = URL.createObjectURL(file);
      setPlatform(prev => ({ ...prev, img: url }));
    }
  };

  // Handle logo file upload for credits
  // eslint-disable-next-line no-unused-vars
  const handleLogoFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      // Store the file for later upload
      setLogoFiles(prev => ({ ...prev, [index]: file }));
      
      // Update the credit item with a temporary URL for preview
      const tempUrl = URL.createObjectURL(file);
      const updatedCredits = [...controlsJson.credits];
      updatedCredits[index] = { ...updatedCredits[index], tempLogoUrl: tempUrl };
      setControlsJson({ ...controlsJson, credits: updatedCredits });
    }
  };

  // Control mapping functions
  const addControlMapping = (player) => {
    const updatedControls = { ...controlsJson.controls };
    updatedControls[player] = [...updatedControls[player], { key: '', action: '' }];
    setControlsJson({ ...controlsJson, controls: updatedControls });
  };

  const removeControlMapping = (player, index) => {
    const updatedControls = { ...controlsJson.controls };
    updatedControls[player] = updatedControls[player].filter((_, i) => i !== index);
    setControlsJson({ ...controlsJson, controls: updatedControls });
  };

  const updateControlMapping = (player, index, field, value) => {
    const updatedControls = { ...controlsJson.controls };
    updatedControls[player][index][field] = value;
    setControlsJson({ ...controlsJson, controls: updatedControls });
  };

  // Notice functions
  const addNotice = () => {
    setControlsJson({
      ...controlsJson,
      notices: [...controlsJson.notices, { text: '', type: 'hint' }]
    });
  };

  const removeNotice = (index) => {
    setControlsJson({
      ...controlsJson,
      notices: controlsJson.notices.filter((_, i) => i !== index)
    });
  };

  const updateNotice = (index, field, value) => {
    const updatedNotices = [...controlsJson.notices];
    updatedNotices[index][field] = value;
    setControlsJson({ ...controlsJson, notices: updatedNotices });
  };

  // Credits functions - now working with credit IDs
  const addExistingCredit = (creditId) => {
    if (!selectedCreditIds.includes(creditId)) {
      setSelectedCreditIds([...selectedCreditIds, creditId]);
    }
  };

  const removeSelectedCredit = (creditId) => {
    setSelectedCreditIds(selectedCreditIds.filter(id => id !== creditId));
  };

  const addNewCredit = () => {
    setNewCredits([
      ...newCredits,
      {
        name: '',
        logo: null,
        website: '',
        github: '',
        tempId: `new_${Date.now()}`
      }
    ]);
  };

  const removeNewCredit = (tempId) => {
    setNewCredits(newCredits.filter(c => c.tempId !== tempId));
    // Also remove any associated logo file
    const newLogoFiles = { ...logoFiles };
    delete newLogoFiles[`new_credit_${tempId}`];
    setLogoFiles(newLogoFiles);
  };

  const updateNewCredit = (tempId, field, value) => {
    setNewCredits(newCredits.map(c => 
      c.tempId === tempId ? { ...c, [field]: value } : c
    ));
  };

  const handleNewCreditLogoChange = (tempId, e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFiles({
        ...logoFiles,
        [`new_credit_${tempId}`]: file
      });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        updateNewCredit(tempId, 'logoPreview', event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Timeline functions
  const addTimelineEntry = () => {
    setPlatform({
      ...platform,
      timeline: [
        ...platform.timeline,
        {
          year: '',
          title: '',
          description: '',
          image: ''
        }
      ]
    });
  };

  const removeTimelineEntry = (index) => {
    setPlatform({
      ...platform,
      timeline: platform.timeline.filter((_, i) => i !== index)
    });
  };

  const updateTimelineEntry = (index, field, value) => {
    const updatedTimeline = [...platform.timeline];
    updatedTimeline[index][field] = value;
    setPlatform({
      ...platform,
      timeline: updatedTimeline
    });
  };

  const handleTimelineImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      // Store the file for later upload
      setLogoFiles(prev => ({ ...prev, [`timeline_${index}`]: file }));
      
      // Update the timeline entry with a temporary URL for preview
      const tempUrl = URL.createObjectURL(file);
      const updatedTimeline = [...platform.timeline];
      updatedTimeline[index] = { ...updatedTimeline[index], image: tempUrl, tempImageUrl: tempUrl };
      setPlatform({ ...platform, timeline: updatedTimeline });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setError('');
    
    // Check if an image file is selected, as it's required by the database
    if (!file) {
      setError('Please select a platform image. This is a required field.');
      return;
    }
    
    try {
      // Start with current timeline
      let finalTimeline = [...platform.timeline];
      
      // First, upload any timeline images that need to be uploaded
      const timelineImageUploads = [];
      platform.timeline.forEach((entry, index) => {
        const timelineFile = logoFiles[`timeline_${index}`];
        if (timelineFile && entry.image && entry.image.startsWith('blob:')) {
          timelineImageUploads.push({ index, file: timelineFile });
        }
      });
      
      if (timelineImageUploads.length > 0) {
        console.log(`Uploading ${timelineImageUploads.length} timeline images...`);
        const timelineFormData = new FormData();
        
        timelineImageUploads.forEach(({ index, file }) => {
          timelineFormData.append(`logo_timeline_${index}`, file);
        });
        
        try {
          const uploadRes = await apiClient.post('/upload-logos', timelineFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (uploadRes.data && uploadRes.data.logos) {
            console.log('Timeline images uploaded:', uploadRes.data.logos);
            
            // Update timeline entries with server URLs
            timelineImageUploads.forEach(({ index }) => {
              const serverPath = uploadRes.data.logos[`timeline_${index}`];
              if (serverPath) {
                finalTimeline[index] = {
                  ...finalTimeline[index],
                  image: serverPath
                };
                console.log(`Updated timeline entry ${index} with server path: ${serverPath}`);
              }
            });
            
            // Update state for UI (but use finalTimeline variable for form submission)
            setPlatform(prev => ({ ...prev, timeline: finalTimeline }));
          }
        } catch (uploadErr) {
          console.error('Error uploading timeline images:', uploadErr);
          setError('Error uploading timeline images. Please try again.');
          return;
        }
      }
      
      // Create any new credits and get their IDs
      const createdCreditIds = [];
      
      for (const newCredit of newCredits) {
        const creditFormData = new FormData();
        creditFormData.append('name', newCredit.name || '');
        creditFormData.append('github', newCredit.github || '');
        creditFormData.append('website', newCredit.website || '');
        
        // Add logo file if exists
        const logoFile = logoFiles[`new_credit_${newCredit.tempId}`];
        if (logoFile) {
          creditFormData.append('logo', logoFile);
        }
        
        try {
          const creditRes = await apiClient.post('/credits', creditFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (creditRes.data.id) {
            createdCreditIds.push(creditRes.data.id);
            console.log(`Created new credit with ID: ${creditRes.data.id}`);
          }
        } catch (creditErr) {
          console.error('Error creating credit:', creditErr);
          alert(`Error creating credit "${newCredit.name}". Continuing with others...`);
        }
      }
      
      // Combine selected existing credits with newly created ones
      const allCreditIds = [...selectedCreditIds, ...createdCreditIds];
      console.log("All credit IDs to save:", allCreditIds);
      
      const formData = new FormData();
      
      // Add all text fields, even if empty
      Object.keys(platform).forEach(key => {
        // Skip the img field as it's handled separately
        if (key !== 'img' && key !== 'ctrljson' && key !== 'timeline') {
          formData.append(key, platform[key] || '');
        }
      });
      
      // Add timeline data (clean up temporary URLs)
      // Use finalTimeline which has the uploaded server paths
      const cleanTimeline = finalTimeline.map(entry => {
        const { tempImageUrl, ...cleanEntry } = entry;
        return cleanEntry;
      });
      formData.append('timeline', JSON.stringify(cleanTimeline));
      console.log('Timeline data being submitted:', cleanTimeline);
      
      // Add a dummy code field since backend still requires it but we've removed it from UI
      // Use timestamp to ensure uniqueness
      formData.append('code', `auto_${Date.now()}`);
      
      // Update the controlsJson with credit IDs
      const finalControlsJson = {
        controls: controlsJson.controls || {
          player1: [],
          player2: []
        },
        notices: controlsJson.notices || [],
        credits: [], // Empty as requested
        creditIds: allCreditIds // Use credit IDs instead
      };
      
      // Convert the JSON object to a string (matching the approach in UpdatePlatform.js)
      const jsonString = JSON.stringify(finalControlsJson, null, 2);
      console.log(`Stringified JSON (${jsonString.length} chars)`, jsonString.substring(0, 200) + '...');
      
      // Send the JSON as a plain string field, not as a file
      formData.append('controlsJson', jsonString);

      // Add compression option
      formData.append('compressed', compressImage ? '1' : '0');

      // Add image file
      if (file) {
        console.log(`Adding platform image file: ${file.name}, size: ${formatFileSize(file.size)}, type: ${file.type}`);
        formData.append('img', file);
      }

      // Log all form data entries for verification
      console.log('Form data entries:');
      for (let pair of formData.entries()) {
        const value = typeof pair[1] === 'string' && pair[1].length > 100 
          ? `${pair[1].substring(0, 100)}... (length: ${pair[1].length})` 
          : pair[1] instanceof File 
            ? `File: ${pair[1].name}, size: ${formatFileSize(pair[1].size)}, type: ${pair[1].type}` 
            : pair[1];
        console.log(`${pair[0]}: ${value}`);
      }

      console.log(`Sending POST request to ${API_BASE_URL}/platforms`);
      const response = await apiClient.post(`${API_BASE_URL}/platforms`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Server response:', response.data);
      navigate("/manage");
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err.response) {
        console.error('Server error:', err.response.data);
        setError(`Server error: ${err.response.data.error || 'Unknown error'}`);
      } else {
        setError(`Error: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const renderControls = () => (
    <div className="controls-editor">
      <h3>玩家按键映射</h3>
      
      <div className="player-section">
        <h4>玩家一</h4>
        <table className="controls-table">
          <thead>
            <tr>
              <th>按键</th>
              <th>功能</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {controlsJson.controls.player1.map((mapping, index) => (
              <tr key={index}>
                <td>
                  <input 
                    type="text" 
                    value={mapping.key}
                    onChange={(e) => updateControlMapping('player1', index, 'key', e.target.value)}
                    placeholder="如：↑, Z, Enter"
                  />
                </td>
                <td>
                  <input 
                    type="text" 
                    value={mapping.action}
                    onChange={(e) => updateControlMapping('player1', index, 'action', e.target.value)}
                    placeholder="如：上, A键, 开始"
                  />
                </td>
                <td>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeControlMapping('player1', index)}
                  >
                    删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button 
          type="button" 
          className="add-button control-btn"
          onClick={() => addControlMapping('player1')}
        >
          添加按键
        </button>
      </div>
      
      <div className="player-section">
        <h4>玩家二</h4>
        <table className="controls-table">
          <thead>
            <tr>
              <th>按键</th>
              <th>功能</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {controlsJson.controls.player2.map((mapping, index) => (
              <tr key={index}>
                <td>
                  <input 
                    type="text" 
                    value={mapping.key}
                    onChange={(e) => updateControlMapping('player2', index, 'key', e.target.value)}
                    placeholder="如：W, G, 1"
                  />
                </td>
                <td>
                  <input 
                    type="text" 
                    value={mapping.action}
                    onChange={(e) => updateControlMapping('player2', index, 'action', e.target.value)}
                    placeholder="如：上, A键, 开始"
                  />
                </td>
                <td>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeControlMapping('player2', index)}
                  >
                    删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button 
          type="button" 
          className="add-button control-btn"
          onClick={() => addControlMapping('player2')}
        >
          添加按键
        </button>
      </div>
    </div>
  );

  const renderNotices = () => (
    <div className="notices-editor">
      <h3>提示信息</h3>
      
      {controlsJson.notices.map((notice, index) => (
        <div key={index} className="notice-item">
          <div className="notice-content">
            <select 
              value={notice.type}
              onChange={(e) => updateNotice(index, 'type', e.target.value)}
            >
              <option value="hint">提示</option>
              <option value="warning">警告</option>
              <option value="info">信息</option>
            </select>
            
            <textarea 
              value={notice.text}
              onChange={(e) => updateNotice(index, 'text', e.target.value)}
              placeholder="输入提示或说明文本"
              rows="2"
            />
            
            <div className={`notice-type-preview ${notice.type}`}>
              {notice.text || `这里将显示${notice.type === 'hint' ? '提示' : notice.type === 'info' ? '信息' : '警告'}内容...`}
            </div>
          </div>
          
          <button 
            type="button" 
            className="remove-button"
            onClick={() => removeNotice(index)}
          >
            删除
          </button>
        </div>
      ))}
      
      <button 
        type="button" 
        className="add-button control-btn"
        onClick={addNotice}
      >
        添加提示
      </button>
    </div>
  );

  const renderCredits = () => {
    // Get selected credit objects from IDs
    const selectedCredits = selectedCreditIds
      .map(id => availableCredits.find(c => c.id === id))
      .filter(c => c !== undefined);
    
    return (
      <div className="credits-editor">
        <h3>关于与致谢</h3>
        <p className="credits-description">
          选择已有的致谢项或创建新的致谢项。致谢项将在游戏页面显示。
        </p>
        
        {/* Selected existing credits */}
        {selectedCredits.length > 0 && (
          <div className="selected-credits-section">
            <h4>已选择的致谢项</h4>
            {selectedCredits.map((credit) => (
              <div key={credit.id} className="credit-item selected-credit">
                <div className="credit-header">
                  <div className="credit-info">
                    {credit.logo && (
                      <img 
                        src={`${API_BASE_URL}${credit.logo}`}
                        alt={credit.name || ''} 
                        className="credit-logo-small"
                      />
                    )}
                    {credit.name && <span className="credit-name">{credit.name}</span>}
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeSelectedCredit(credit.id)}
                  >
                    移除
                  </button>
                </div>
                <div className="credit-details">
                  {credit.website && <span>{credit.website}</span>}
                  {credit.github && <span>{credit.github}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add existing credit dropdown */}
        <div className="add-existing-credit">
          <h4>添加已有致谢项</h4>
          <select 
            onChange={(e) => {
              if (e.target.value) {
                addExistingCredit(parseInt(e.target.value));
                e.target.value = ''; // Reset selection
              }
            }}
            className="credit-select"
          >
            <option value="">-- 选择一个致谢项 --</option>
            {availableCredits
              .filter(c => !selectedCreditIds.includes(c.id))
              .map(credit => (
                <option key={credit.id} value={credit.id}>
                  {credit.name || credit.github || credit.website || `Credit #${credit.id}`}
                </option>
              ))}
          </select>
        </div>
        
        {/* New credits being created */}
        {newCredits.length > 0 && (
          <div className="new-credits-section">
            <h4>新建致谢项</h4>
            {newCredits.map((credit) => (
              <div key={credit.tempId} className="credit-item new-credit">
                <div className="credit-header">
                  <input 
                    type="text"
                    placeholder="项目名称" 
                    value={credit.name}
                    onChange={(e) => updateNewCredit(credit.tempId, 'name', e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeNewCredit(credit.tempId)}
                  >
                    删除
                  </button>
                </div>
                
                <div className="credit-logo">
                  {credit.logoPreview && (
                    <div className="logo-preview">
                      <img 
                        src={credit.logoPreview}
                        alt={credit.name || ''} 
                        className="logo-image-preview"
                      />
                    </div>
                  )}
                  
                  <div className="file-input logo-upload">
                    <label className="file-label">
                      上传标志
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleNewCreditLogoChange(credit.tempId, e)}
                      />
                    </label>
                  </div>
                </div>
                
                <div className="credit-links">
                  <div className="link-item">
                    <label>网站:</label>
                    <input 
                      type="text"
                      placeholder="官方网站 URL" 
                      value={credit.website}
                      onChange={(e) => updateNewCredit(credit.tempId, 'website', e.target.value)}
                    />
                  </div>
                  
                  <div className="link-item">
                    <label>GitHub:</label>
                    <input 
                      type="text"
                      placeholder="GitHub 仓库 URL" 
                      value={credit.github}
                      onChange={(e) => updateNewCredit(credit.tempId, 'github', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <button 
          type="button" 
          className="add-button control-btn"
          onClick={addNewCredit}
        >
          创建新致谢项
        </button>
      </div>
    );
  };

  const renderTimeline = () => (
    <div className="timeline-editor">
      <h3>平台发展时间线</h3>
      <p className="timeline-description">
        添加平台的重要发展节点，包括发布年份、标题、描述和相关图片。
      </p>
      
      {platform.timeline.map((entry, index) => (
        <div key={index} className="timeline-item">
          <div className="timeline-header">
            <span className="timeline-number">{index + 1}</span>
            <h4>时间线条目 {index + 1}</h4>
            <button 
              type="button" 
              className="remove-button"
              onClick={() => removeTimelineEntry(index)}
            >
              ✕
            </button>
          </div>
          
          <div className="timeline-content">
            <div className="timeline-row">
              <div className="form-group timeline-year">
                <label>年份:</label>
                <input 
                  type="number"
                  placeholder="1985" 
                  value={entry.year}
                  onChange={(e) => updateTimelineEntry(index, 'year', e.target.value)}
                  min="1900"
                  max="2030"
                />
              </div>
              
              <div className="form-group timeline-title">
                <label>标题:</label>
                <input 
                  type="text"
                  placeholder="重要事件标题" 
                  value={entry.title}
                  onChange={(e) => updateTimelineEntry(index, 'title', e.target.value)}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>描述:</label>
              <textarea 
                placeholder="描述这个时间点发生的重要事件..."
                value={entry.description}
                onChange={(e) => updateTimelineEntry(index, 'description', e.target.value)}
                rows="3"
              />
            </div>
            
            <div className="form-group timeline-image-group">
              <label>相关图片:</label>
              <div className="timeline-image-section">
                <div className="file-input timeline-image-upload">
                  <label className="file-label">
                    <span className="upload-icon">📁</span>
                    {entry.image ? '更换图片' : '选择图片'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleTimelineImageChange(index, e)}
                    />
                  </label>
                </div>
                
                {entry.image && (
                  <div className="image-preview-container">
                    <img 
                      src={entry.image.startsWith('blob:') || entry.image.startsWith('data:') ? 
                        entry.image : 
                        `${API_BASE_URL}${entry.image}`} 
                      alt={`Timeline ${entry.year} - ${entry.title}`} 
                      className="timeline-image-preview"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      
      <button 
        type="button" 
        className="add-button timeline-add-btn"
        onClick={addTimelineEntry}
      >
        <span className="add-icon">+</span>
        添加时间线条目
      </button>
    </div>
  );

  return (
    <div className="add-container">
      <h1>Add New Platform</h1>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Chinese Name:</label>
          <input
            type="text"
            name="namechn"
            value={platform.namechn}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>English Name:</label>
          <input
            type="text"
            name="nameeng"
            value={platform.nameeng}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Emulator: *</label>
          <select
            name="emulator"
            value={platform.emulator}
            onChange={handleChange}
            required
            className="emulator-select"
          >
            {AVAILABLE_EMULATORS.map(emulator => (
              <option key={emulator.value} value={emulator.value}>
                {emulator.label}
              </option>
            ))}
          </select>
        </div>

        {/* Always show Emulator Options field for all emulators */}
        <div className="form-group">
          <label>Emulator Options:</label>
          <input
            type="text"
            name="emulatoroptions"
            value={platform.emulatoroptions}
            onChange={handleChange}
          />
          <small className="form-text">
            {platform.emulator === 'ejs' 
              ? 'For EmulatorJS: Enter the core name (snes, n64, etc.)' 
              : 'Optional parameters specific to this emulator'}
          </small>
        </div>

        <div className="form-group">
          <label>Description:</label>
          <textarea
            name="description"
            value={platform.description}
            onChange={handleChange}
            rows="4"
          />
        </div>

        <div className="form-group">
          <label>Style:</label>
          <select
            name="style"
            value={platform.style}
            onChange={handleChange}
            className="emulator-select"
          >
            {AVAILABLE_STYLES.map(style => (
              <option key={style.value} value={style.value}>
                {style.label}
              </option>
            ))}
          </select>
          <small className="form-text">Visual style for the platform display</small>
        </div>

        <div className="form-group">
          <label>Default Year:</label>
          <input
            type="number"
            name="defaultyear"
            value={platform.defaultyear}
            onChange={handleChange}
            placeholder="Default year for games (e.g., 1985)"
          />
          <small className="form-text">Default release year to use for games on this platform</small>
        </div>

        <div className="form-group">
          <label>Platform Image:</label>
          <div className="input-group">
            <input
              type="text"
              name="img"
              value={platform.img}
              onChange={handleChange}
              placeholder="Platform image URL or blob"
              readOnly
            />
            <div className="file-input">
              <label className="file-label">
                {fileName || 'Upload File'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          </div>
          {fileSize && <div className="file-size-info">{fileSize}</div>}
          <div className="compress-option">
            <input
              type="checkbox"
              id="compress-image"
              checked={compressImage}
              onChange={(e) => setCompressImage(e.target.checked)}
            />
            <label htmlFor="compress-image">Compress image (uses TinyPNG)</label>
          </div>
          {platform.img && (
            <div className="image-preview-container">
              <img src={platform.img} alt="Platform preview" className="preview" />
            </div>
          )}
        </div>

        {/* Controls JSON Editor Section */}
        <div className="controls-json-editor">
          <h2>控制与提示界面</h2>
          <div className="editor-tabs">
            <button 
              type="button"
              className={`editor-tab ${activeSection === 'controls' ? 'active' : ''}`}
              onClick={() => setActiveSection('controls')}
            >
              按键映射
            </button>
            <button 
              type="button"
              className={`editor-tab ${activeSection === 'notices' ? 'active' : ''}`}
              onClick={() => setActiveSection('notices')}
            >
              提示信息
            </button>
            <button 
              type="button"
              className={`editor-tab ${activeSection === 'credits' ? 'active' : ''}`}
              onClick={() => setActiveSection('credits')}
            >
              致谢信息
            </button>
            <button 
              type="button"
              className={`editor-tab ${activeSection === 'timeline' ? 'active' : ''}`}
              onClick={() => setActiveSection('timeline')}
            >
              平台时间线
            </button>
          </div>
          
          <div className="editor-content">
            {activeSection === 'controls' && renderControls()}
            {activeSection === 'notices' && renderNotices()}
            {activeSection === 'credits' && renderCredits()}
            {activeSection === 'timeline' && renderTimeline()}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="submit-btn">Add Platform</button>
      </form>
    </div>
  );
};

export default AddPlatform; 